import {
  AdjustmentsHorizontalIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BeakerIcon,
  BriefcaseIcon,
  BugAntIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  QuestionMarkCircleIcon,
  SunIcon,
  UserCircleIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Image,
  Indicator,
  Loader,
  Menu,
  rem,
  Switch,
  Text,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { FC, ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { ROUTES } from "../../constants/routes";
import { useColors } from "../../hooks/useColors";
import { useHeaderNavSpotlight } from "../../hooks/useHeaderNavSpotlight";
import { useShowExperimental } from "../../hooks/useShowExperimental";
import { useTranslation } from "../../hooks/useTranslation";
import { getRoleName, UserRole } from "../../models/user.model";
import { GraphService } from "../../services/graphService";
import { AVAILABLE_LANGUAGES } from "../../services/translationService";
import { Z_INDEX_LAYERS } from "../../utils/zIndexLayers";
import { LoginButton } from "../common/LoginButton";
import classes from "./Header.module.css";

// Type declarations for HeroIcons
type HeroIcon = React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;

interface NavItemConfig {
  key: string;
  path: string;
  Icon: HeroIcon;
  shortLabel: string;
  fullLabel: string;
  match: boolean;
}

export const Header: FC = (): ReactElement => {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAuthenticated, isAdmin, isManager, userRole } = useAuth();
  const { showExperimental, toggle: toggleExperimental } = useShowExperimental();
  const { t, changeLanguage, currentLanguage, isChangingLanguage } =
    useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  // Media query for responsive design — collapse nav at ≤992px
  const isSmallScreen = useMediaQuery("(max-width: 992px)");

  // Guarantees every nav item stays reachable even when shortened labels still
  // don't fit (longer-language translations, a narrow desktop window, a future
  // added item): falls back to the same hamburger used for isSmallScreen.
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const [navOverflowing, setNavOverflowing] = useState(false);
  const resizeRecheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showDesktopNav = !isSmallScreen && !navOverflowing;
  const showHamburger = isSmallScreen || navOverflowing;

  // One-time "My CV / My Skills moved" callout for Manager/Admin, dismissed
  // permanently (per browser) on first acknowledgement.
  const { showSpotlight, dismiss: dismissSpotlight } = useHeaderNavSpotlight(
    isAuthenticated && (isAdmin || isManager)
  );

  const colors = useColors();

  // Fetch user photo when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserPhoto();
    }
  }, [isAuthenticated]);

  const fetchUserPhoto = async () => {
    if (!isAuthenticated) return;

    try {
      setLoadingPhoto(true);
      const graphService = new GraphService();
      // 10-second guard: if the Graph backend call hangs (e.g. token acquisition stall)
      // we still clear the spinner so the avatar menu remains usable.
      const photoTimeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000));
      const photoUrl = await Promise.race([
        graphService.getCurrentUserPhoto(),
        photoTimeout,
      ]);
      setUserPhotoUrl(photoUrl);
    } catch (error) {
      console.error("Error fetching user photo:", error);
    } finally {
      setLoadingPhoto(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.HOME);
  };

  const toggleColorScheme = () => {
    const nextScheme = isDark ? "light" : "dark";
    setColorScheme(nextScheme);
  };

  const handleLanguageChange = async (lang: string) => {
    if (lang && lang !== currentLanguage && !isChangingLanguage) {
      await changeLanguage(lang);
    }
  };

  const renderIcon = (Icon: HeroIcon, size: number = 18): ReactElement => (
    <Icon width={size} style={{ marginRight: "6px" }} />
  );

  // Get color for role badge
  const getRoleBadgeColor = (): string => {
    if (!userRole) return "gray";

    switch (userRole) {
      case UserRole.Admin:
        return "red";
      case UserRole.Manager:
        return "blue";
      case UserRole.User:
        return "green";
      default:
        return "gray";
    }
  };

  // Create click handlers for navigation items
  const handleNavigate = (path: string, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
    }
    navigate(path);
  };

  // Single source of truth for nav items — shared by the desktop bar (short
  // label + tooltip) and the mobile/hamburger dropdown (full label, no
  // tooltip). Manager/Admin never see My CV / My Skills here — those move
  // into the profile menu for those roles (see Menu.Dropdown below).
  const navItems = useMemo((): NavItemConfig[] => {
    const items: NavItemConfig[] = [
      {
        key: "overview",
        path: ROUTES.CV_LIST,
        Icon: MagnifyingGlassIcon,
        shortLabel: t("HEADER_COMPANY_OVERVIEW_SHORT", undefined, "Overview"),
        fullLabel: t("HEADER_COMPANY_OVERVIEW"),
        match: location.pathname === ROUTES.CV_LIST,
      },
      {
        key: "faq",
        path: ROUTES.FAQ,
        Icon: QuestionMarkCircleIcon,
        shortLabel: t("HEADER_FAQ_SHORT", undefined, "FAQ"),
        fullLabel: t("HEADER_FAQ", undefined, "FAQ"),
        match: location.pathname.startsWith(ROUTES.FAQ),
      },
    ];

    if (userRole === UserRole.User) {
      items.push(
        {
          key: "my-cv",
          path: ROUTES.CV_EDITOR,
          Icon: DocumentTextIcon,
          shortLabel: t("HEADER_MY_CV"),
          fullLabel: t("HEADER_MY_CV"),
          match: location.pathname === ROUTES.CV_EDITOR,
        },
        {
          key: "my-skills",
          path: ROUTES.MY_SKILLS,
          Icon: WrenchScrewdriverIcon,
          shortLabel: t("HEADER_MY_SKILLS"),
          fullLabel: t("HEADER_MY_SKILLS"),
          match: location.pathname === ROUTES.MY_SKILLS,
        }
      );
    }

    if (isAdmin || isManager) {
      items.push({
        key: "talent-marketplace",
        path: ROUTES.OPPORTUNITIES,
        Icon: BriefcaseIcon,
        shortLabel: t("HEADER_TALENT_MARKETPLACE", undefined, "Talent Marketplace"),
        fullLabel: t("HEADER_TALENT_MARKETPLACE", undefined, "Talent Marketplace"),
        match: location.pathname.startsWith(ROUTES.OPPORTUNITIES),
      });
    }

    if (isAdmin || isManager) {
      items.push(
        {
          key: "employees",
          path: ROUTES.EMPLOYEES,
          Icon: UserGroupIcon,
          shortLabel: t("HEADER_EMPLOYEES_SHORT", undefined, "Employees"),
          fullLabel: t("HEADER_EMPLOYEES", undefined, "Employees"),
          match: location.pathname.startsWith(ROUTES.EMPLOYEES),
        },
        {
          key: "reports",
          path: ROUTES.REPORTS,
          Icon: ChartBarIcon,
          shortLabel: t("HEADER_REPORTS_SHORT", undefined, "Reports"),
          fullLabel: t("HEADER_REPORTS", undefined, "Reports"),
          match: location.pathname.startsWith(ROUTES.REPORTS),
        },
        {
          key: "chat",
          path: ROUTES.CHAT_ABOUT_CANDIDATES,
          Icon: ChatBubbleLeftRightIcon,
          shortLabel: t("HEADER_CHAT_ABOUT_CANDIDATES_SHORT", undefined, "Chat"),
          fullLabel: t("HEADER_CHAT_ABOUT_CANDIDATES"),
          match: location.pathname.startsWith(ROUTES.CHAT_ABOUT_CANDIDATES),
        },
        {
          key: "operations",
          path: ROUTES.OPERATIONS,
          Icon: AdjustmentsHorizontalIcon,
          shortLabel: t("HEADER_ADMIN_PANEL_SHORT", undefined, "Operations"),
          fullLabel: t("HEADER_ADMIN_PANEL"),
          match: location.pathname.startsWith(ROUTES.OPERATIONS),
        }
      );
    }

    return items;
  }, [t, currentLanguage, userRole, isAdmin, isManager, location.pathname]);

  // Re-attempt showing the desktop bar whenever we cross back above the
  // hamburger breakpoint — the layout effect below corrects it back to
  // hamburger if content still doesn't fit.
  useEffect(() => {
    if (!isSmallScreen) {
      setNavOverflowing(false);
    }
  }, [isSmallScreen]);

  // Debounced window-resize re-check for the case where isSmallScreen never
  // changes (still "desktop" width) but content no longer fits — e.g. a
  // longer-language translation, or the window shrinking within the desktop
  // range.
  useEffect(() => {
    const scheduleRecheck = () => {
      if (resizeRecheckTimeout.current) {
        clearTimeout(resizeRecheckTimeout.current);
      }
      resizeRecheckTimeout.current = setTimeout(() => {
        if (!isSmallScreen) {
          setNavOverflowing(false);
        }
      }, 150);
    };

    window.addEventListener("resize", scheduleRecheck);
    return () => {
      window.removeEventListener("resize", scheduleRecheck);
      if (resizeRecheckTimeout.current) {
        clearTimeout(resizeRecheckTimeout.current);
      }
    };
  }, [isSmallScreen]);

  // Measures the desktop nav bar right after paint and flips to the
  // hamburger fallback if it doesn't actually fit. Runs whenever nav content
  // could have changed width (language, role, feature flag, route).
  useLayoutEffect(() => {
    if (isSmallScreen) {
      return;
    }
    const el = desktopNavRef.current;
    if (!el) {
      return;
    }
    const isOverflowing = el.scrollWidth > el.clientWidth + 1;
    if (isOverflowing !== navOverflowing) {
      setNavOverflowing(isOverflowing);
    }
    // navOverflowing intentionally excluded — this effect is what sets it,
    // including it would create a redundant re-run on every flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmallScreen, navItems]);

  return (
    <>
      <Box
        component="header"
        className={classes.header}
        style={{
          height: 60,
          padding: "0 1rem",
          borderBottom: `1px solid ${colors.getSemanticColorValue("borderLight")}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: isDark ? "none" : colors.getGradientValue("homepage"),
          position: "relative",
          zIndex: Z_INDEX_LAYERS.HEADER,
        }}
      >
        {/* Logo */}
        <Group gap="sm" align="center" style={{ height: "100%" }}>
          <Box
            onClick={(e) =>
              handleNavigate(isAuthenticated ? ROUTES.CV_LIST : ROUTES.HOME, e)
            }
            style={{
              textDecoration: "none",
              height: "100%",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <Image
              src={isDark ? "/logo_white.svg" : "/logo.svg"}
              style={{
                width: "100%",
                height: "100%",
                maxWidth: "200px",
                maxHeight: "50px",
              }}
              alt="Sopra Steria Logo"
              height={50}
              fit="contain"
              fallbackSrc="https://placehold.co/200x50?text=Logo"
            />
          </Box>
        </Group>

        {/* Desktop nav links — hidden on small screens or when content overflows */}
        {isAuthenticated && (
          <Box
            ref={desktopNavRef}
            style={{
              height: "100%",
              overflow: "hidden",
              minWidth: 0,
              display: showDesktopNav ? "flex" : "none",
            }}
          >
            <Group gap="md" wrap="nowrap" style={{ height: "100%" }}>
              {navItems.map((item) => (
                <Tooltip
                  key={item.key}
                  label={item.fullLabel}
                  disabled={item.shortLabel === item.fullLabel}
                  position="bottom"
                >
                  <Text
                    onClick={(e) => handleNavigate(item.path, e)}
                    fw={item.match ? 600 : 400}
                    className={item.match ? classes.activeNavItem : classes.navItem}
                    aria-label={item.fullLabel}
                    style={{
                      textDecoration: "none",
                      color: isDark ? "white" : "black",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "16px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderIcon(item.Icon)}
                    {item.shortLabel}
                  </Text>
                </Tooltip>
              ))}
            </Group>
          </Box>
        )}

        {/* Right-side controls — always visible */}
        <Group gap="xs" align="center">
          {/* Hamburger — small screens, or when the desktop nav doesn't fit */}
          {showHamburger && isAuthenticated && (
            <Tooltip label={t("HEADER_NAVIGATION") || "Navigation"} position="bottom">
              <ActionIcon
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                variant="subtle"
                size="lg"
                aria-label={t("HEADER_NAVIGATION") || "Navigation"}
              >
                <Bars3Icon style={{ width: 24, height: 24 }} />
              </ActionIcon>
            </Tooltip>
          )}

          {isAuthenticated ? (
            <>
              <Tooltip label={isDark ? t("THEME_LIGHT") || "Light mode" : t("THEME_DARK") || "Dark mode"} position="bottom">
                <ActionIcon
                  variant="default"
                  size="lg"
                  aria-label="Toggle color scheme"
                  onClick={toggleColorScheme}
                >
                  <Center>
                    {isDark ? (
                      <SunIcon style={{ width: rem(18), height: rem(18) }} />
                    ) : (
                      <MoonIcon style={{ width: rem(18), height: rem(18) }} />
                    )}
                  </Center>
                </ActionIcon>
              </Tooltip>

              <Tooltip label={t("REPORT_BUG") || "Report a bug"} position="bottom">
                <ActionIcon
                  component="a"
                  href="mailto:daisy.bugs@soprasteria.com"
                  variant="default"
                  size="lg"
                  aria-label="Report a bug"
                >
                  <Center>
                    <BugAntIcon style={{ width: rem(18), height: rem(18) }} />
                  </Center>
                </ActionIcon>
              </Tooltip>

              <Menu shadow="md" position="bottom-end" onOpen={dismissSpotlight}>
                <Menu.Target>
                  <Indicator
                    disabled={!showSpotlight}
                    processing
                    color="brand.5"
                    size={10}
                    offset={4}
                  >
                    <Box
                      className={classes.avatarButton}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "3px 6px 3px 3px",
                        borderRadius: 999,
                      }}
                    >
                      {loadingPhoto ? (
                        <Loader size="sm" />
                      ) : (
                        <Avatar
                          src={userPhotoUrl}
                          size={36}
                          radius="xl"
                          color="blue"
                        />
                      )}
                      <ChevronDownIcon
                        style={{
                          width: 14,
                          height: 14,
                          opacity: 0.6,
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </Indicator>
                </Menu.Target>
                <Menu.Dropdown>
                  {userRole && (
                    <Menu.Label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {t("ROLE")}
                      <Badge
                        color={getRoleBadgeColor()}
                        size="sm"
                        style={{ marginLeft: 8 }}
                      >
                        {getRoleName(userRole)}
                      </Badge>
                    </Menu.Label>
                  )}
                  <Menu.Item
                    leftSection={renderIcon(UserCircleIcon)}
                    onClick={() => navigate(ROUTES.PROFILE)}
                  >
                    {t("PROFILE")}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={renderIcon(DocumentTextIcon)}
                    onClick={() => navigate(ROUTES.CHANGELOG)}
                  >
                    {t("HEADER_CHANGELOG")}
                  </Menu.Item>
                  {isAdmin && (
                    <Menu.Item
                      leftSection={renderIcon(BeakerIcon)}
                      rightSection={
                        <Switch
                          size="xs"
                          checked={showExperimental}
                          onChange={toggleExperimental}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      closeMenuOnClick={false}
                      onClick={toggleExperimental}
                    >
                      {t("HEADER_SHOW_EXPERIMENTAL") || "Experimental features"}
                    </Menu.Item>
                  )}
                  {(isAdmin || isManager) && (
                    <>
                      <Menu.Divider />
                      <Menu.Item
                        leftSection={renderIcon(DocumentTextIcon)}
                        onClick={() => navigate(ROUTES.CV_EDITOR)}
                      >
                        {t("HEADER_MY_CV")}
                      </Menu.Item>
                      <Menu.Item
                        leftSection={renderIcon(WrenchScrewdriverIcon)}
                        onClick={() => navigate(ROUTES.MY_SKILLS)}
                      >
                        {t("HEADER_MY_SKILLS")}
                      </Menu.Item>
                    </>
                  )}
                  <Menu.Divider />
                  <Menu.Label>{t("LANGUAGE") || "Language"}</Menu.Label>
                  {Object.entries(
                    AVAILABLE_LANGUAGES as Record<string, string>
                  ).map(([code, name]) => (
                    <Menu.Item
                      key={code}
                      disabled={isChangingLanguage || code === currentLanguage}
                      onClick={() => handleLanguageChange(code)}
                      style={{ fontWeight: code === currentLanguage ? 600 : 400 }}
                    >
                      {code === currentLanguage ? `✓ ${name}` : `   ${name}`}
                    </Menu.Item>
                  ))}
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={renderIcon(ArrowRightOnRectangleIcon)}
                    onClick={handleLogout}
                  >
                    {t("HEADER_LOGOUT")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </>
          ) : (
            <LoginButton />
          )}
        </Group>

        {/* Mobile/tablet nav dropdown — absolutely positioned below header, solid background */}
        {showHamburger && mobileMenuOpen && isAuthenticated && (
          <Box
            style={{
              position: "absolute",
              top: 60,
              left: 0,
              right: 0,
              zIndex: Z_INDEX_LAYERS.HEADER_DROPDOWN,
              backgroundColor: isDark
                ? "var(--mantine-color-dark-7)"
                : "white",
              borderBottom: `1px solid ${colors.getSemanticColorValue(
                "borderLight"
              )}`,
              padding: "0.5rem 1rem 1rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            {navItems.map((item, idx) => (
              <Text
                key={item.key}
                onClick={(e) => {
                  handleNavigate(item.path, e);
                  setMobileMenuOpen(false);
                }}
                fw={item.match ? 600 : 400}
                className={item.match ? classes.activeNavItem : classes.navItem}
                style={{
                  textDecoration: "none",
                  color: isDark ? "white" : "black",
                  display: "flex",
                  alignItems: "center",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "10px 0",
                  borderBottom:
                    idx < navItems.length - 1
                      ? `1px solid ${colors.getSemanticColorValue("borderLight")}`
                      : "none",
                }}
              >
                {renderIcon(item.Icon)}
                {item.fullLabel}
              </Text>
            ))}
          </Box>
        )}

        {/* One-time "My CV / My Skills moved" callout — Manager/Admin only, dismissed permanently after first acknowledgement */}
        {showSpotlight && (
          <Box
            style={{
              position: "absolute",
              top: 68,
              right: 8,
              zIndex: Z_INDEX_LAYERS.HEADER_DROPDOWN,
              maxWidth: 280,
              backgroundColor: isDark ? "var(--mantine-color-dark-7)" : "white",
              border: `1px solid ${colors.getSemanticColorValue("borderLight")}`,
              borderRadius: 8,
              padding: "0.75rem 1rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
            }}
          >
            <Text size="sm" style={{ color: isDark ? "white" : "black" }}>
              {t("HEADER_NAV_UPDATE_MESSAGE")}
            </Text>
            <Button
              size="xs"
              color="brand.5"
              mt="xs"
              onClick={dismissSpotlight}
            >
              {t("HEADER_NAV_UPDATE_DISMISS") || "Got it"}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};
